/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import type { CustomerSaleRow } from '../infra/customer360Repo';
import type { CustomerSalesWindows } from '../screens/hooks/useCustomerAppointments';
import type { CustomerSalesSummary } from '../infra/customerRepository';
import { formatMoney } from '../../shared/format/public-api';
// Import directo al leaf de presentación (no public-api): el barrel de sales
// vuelve a entrar a customers/public-api → require cycle. Mismo patrón que
// `salesNavigation` en CustomerMovementModal.
import { formatSaleShortRefLabel } from '../../sales/application/salePresentation';

type Props = {
  sales: CustomerSaleRow[];
  loading: boolean;
  page: number;
  hasMore: boolean;
  pageSize: number;
  onLoadPage: (page: number) => void;
  totalCount?: number;
  salesSummary?: CustomerSalesSummary | null;
  salesWindows?: CustomerSalesWindows | null;
  mobile?: boolean;
  onViewSale?: (saleId: string) => void;
};

const PAYMENT_STATUS_LABEL: Record<string, string> = {
  paid: 'Pagado',
  pending: 'Pendiente',
  partial: 'Parcial',
  refunded: 'Reembolsado',
  unpaid: 'Sin pagar',
};

const PAYMENT_STATUS_COLOR: Record<string, string> = {
  paid: ticketColors.ok,
  pending: ticketColors.gold,
  partial: ticketColors.gold,
  refunded: ticketColors.ink3,
  unpaid: ticketColors.bad,
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function CustomerSalesHistorySection({
  sales,
  loading,
  page,
  hasMore,
  pageSize,
  onLoadPage,
  totalCount,
  salesSummary,
  salesWindows,
  mobile = false,
  onViewSale,
}: Props) {
  const hasSales = sales.length > 0;
  const showPager = hasMore || page > 0;
  const showSalesSummary = salesSummary && salesSummary.salesCount > 0;
  const showWindows = salesWindows && (salesWindows.last30.salesCount > 0 || salesWindows.last90.salesCount > 0 || salesWindows.last365.salesCount > 0);

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.sales-history"
    >
      <View style={styles.headerRow}>
        <Ionicons name="receipt-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.title}>Compras</Text>
        {totalCount != null && totalCount > 0 ? (
          <View style={styles.countBadge}>
            <Text style={styles.countText}>{totalCount}</Text>
          </View>
        ) : null}
      </View>

      {showSalesSummary ? (
        <View style={styles.summaryBox} testID="customers.detail.sales-summary">
          <Text style={styles.summaryLine}>
            <Text style={styles.summaryBig}>{formatMoney(salesSummary!.totalSpent)}</Text>
            <Text style={styles.summaryMuted}> gastado en </Text>
            <Text style={styles.summaryBold}>{salesSummary!.salesCount}</Text>
            <Text style={styles.summaryMuted}> {salesSummary!.salesCount === 1 ? 'venta' : 'ventas'}</Text>
          </Text>
          <Text style={styles.summaryLine}>
            <Text style={styles.summaryMuted}>Ticket promedio </Text>
            <Text style={styles.summaryBold}>{formatMoney(salesSummary!.avgTicket)}</Text>
          </Text>
        </View>
      ) : null}

      {showWindows ? (
        <View style={styles.windowsBox} testID="customers.detail.sales-windows">
          <Text style={styles.windowLine}>
            <Text style={styles.windowLabel}>30 días</Text> {formatMoney(salesWindows!.last30.totalSpent)} · {salesWindows!.last30.salesCount} {salesWindows!.last30.salesCount === 1 ? 'venta' : 'ventas'}
          </Text>
          <Text style={styles.windowLine}>
            <Text style={styles.windowLabel}>90 días</Text> {formatMoney(salesWindows!.last90.totalSpent)} · {salesWindows!.last90.salesCount} {salesWindows!.last90.salesCount === 1 ? 'venta' : 'ventas'}
          </Text>
          <Text style={styles.windowLine}>
            <Text style={styles.windowLabel}>365 días</Text> {formatMoney(salesWindows!.last365.totalSpent)} · {salesWindows!.last365.salesCount} {salesWindows!.last365.salesCount === 1 ? 'venta' : 'ventas'}
          </Text>
        </View>
      ) : null}

      {loading ? (
        <Text style={styles.emptyText}>Cargando compras…</Text>
      ) : !hasSales ? (
        <Text style={styles.emptyText}>Sin compras registradas.</Text>
      ) : (
        <View style={styles.list}>
          {sales.map((s) => {
            const statusColor = PAYMENT_STATUS_COLOR[s.paymentStatus] ?? ticketColors.ink3;
            const statusLabel = PAYMENT_STATUS_LABEL[s.paymentStatus] ?? s.paymentStatus;
            // shortId puede venir vacío del RPC (filas viejas); el helper canónico
            // de sales deriva el sufijo del id y evita el `#` pelado.
            const refLabel = s.shortId ? `#${s.shortId}` : formatSaleShortRefLabel(s.id);
            return (
              <TouchableOpacity
                key={s.id}
                style={styles.row}
                onPress={() => onViewSale?.(s.id)}
                disabled={!onViewSale}
                accessibilityRole="button"
                accessibilityLabel={`Venta ${refLabel} ${formatDate(s.createdAt)}`}
                testID={`customers.detail.sale.${s.id}`}
              >
                <View style={styles.rowLeft}>
                  <Text style={styles.rowId}>{refLabel}</Text>
                  <Text style={styles.rowDate}>{formatDate(s.createdAt)}</Text>
                </View>
                <View style={styles.rowRight}>
                  <Text style={styles.rowAmount}>{formatMoney(s.total)}</Text>
                  <View style={[styles.statusPill, { backgroundColor: statusColor + '22' }]}>
                    <Text style={[styles.statusText, { color: statusColor }]}>
                      {statusLabel}
                    </Text>
                  </View>
                  {onViewSale ? (
                    <Ionicons name="chevron-forward" size={14} color={ticketColors.ink3} />
                  ) : null}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      {showPager ? (
        <View style={styles.pagerRow} testID="customers.detail.sales-pager">
          <TouchableOpacity
            style={[styles.pagerBtn, page === 0 && styles.pagerBtnDisabled]}
            onPress={() => page > 0 && onLoadPage(page - 1)}
            disabled={page === 0 || loading}
            accessibilityRole="button"
            accessibilityLabel="Página anterior"
          >
            <Ionicons name="chevron-back" size={16} color={page === 0 ? ticketColors.ink3 : ticketColors.ink} />
            <Text style={[styles.pagerText, page === 0 && styles.pagerTextDisabled]}>Anterior</Text>
          </TouchableOpacity>
          <Text style={styles.pagerInfo}>Pág. {page + 1}</Text>
          <TouchableOpacity
            style={[styles.pagerBtn, !hasMore && styles.pagerBtnDisabled]}
            onPress={() => hasMore && onLoadPage(page + 1)}
            disabled={!hasMore || loading}
            accessibilityRole="button"
            accessibilityLabel="Página siguiente"
          >
            <Text style={[styles.pagerText, !hasMore && styles.pagerTextDisabled]}>Siguiente</Text>
            <Ionicons name="chevron-forward" size={16} color={!hasMore ? ticketColors.ink3 : ticketColors.ink} />
          </TouchableOpacity>
        </View>
      ) : null}
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: {
    padding: spacing.md,
  },
  panelMobile: {
    padding: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  countBadge: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 4,
  },
  countText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink3,
  },
  summaryBox: {
    marginBottom: spacing.xs,
  },
  summaryLine: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 20,
    marginBottom: 2,
  },
  summaryBig: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  summaryBold: {
    fontWeight: '700',
    color: ticketColors.ink,
  },
  summaryMuted: {
    color: ticketColors.ink3,
    fontSize: 13,
  },
  windowsBox: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
    paddingBottom: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  windowLine: {
    fontSize: 12,
    color: ticketColors.ink2,
    backgroundColor: ticketColors.paper2,
    borderRadius: 6,
    paddingHorizontal: spacing.xs,
    paddingVertical: 3,
  },
  windowLabel: {
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  list: {
    gap: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  rowLeft: {
    flex: 1,
    paddingRight: spacing.sm,
  },
  rowId: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink,
  },
  rowDate: {
    fontSize: 12,
    color: ticketColors.ink3,
    marginTop: 2,
  },
  rowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  rowAmount: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  statusPill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: 14,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
    marginTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  pagerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    minHeight: 44,
  },
  pagerBtnDisabled: {
    opacity: 0.4,
  },
  pagerText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  pagerTextDisabled: {
    color: ticketColors.ink3,
  },
  pagerInfo: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontWeight: '500',
  },
});
